import styles from "../../../styles/CoordinationBoards.module.css"
import FBTSidebar from "../../../components/sidebars/FBTSidebar"
import { useContext, useCallback, useState, useEffect } from "react"
import { UserContext } from "../../../context/userContext"
import axios from "axios"
import { coordinationBoardsApiUrl } from "../../../config/microservicesUrls"
import { LoaderMedium } from "../../../components/Loaders"
import { useQuery } from "@apollo/client"
import { Button } from "primereact/button"
import { Checkbox } from "primereact/checkbox"
import { Toast } from "primereact/toast"
import { useRef } from "react"
import { BoardType } from "../Types/Enums"
import { Dropdown } from 'primereact/dropdown'
import { renderTableDataCell, GET_STAFF_NAMES, GET_ORGANIZATIONS_AND_REFERRALS, showError, getActualDate, findName } from "../Utils/Utils"
import CrossOrganizationBoardForm from "./CrossOrganizationBoardForm"
import ReferralPartnerBoardForm from "./ReferralPartnerBoardForm"
import { partnerDefaultEnterpriseId } from "../../../config/referralsSettings"
import { topicStatusChangeOptions } from "../Types/TypeUtils"

const api_url = coordinationBoardsApiUrl()

const Configuration = () => {
  const userCtx = useContext(UserContext)
  const [loading, setLoading] = useState(true)
  const toast = useRef(null)
  const [boards, setBoards] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [organizationsDic, setOrganizationsDic] = useState({})
  const [crossOrgBoardDialogVisible, setCrossOrgBoardDialogVisible] = useState(false)
  const [loadingCrossOrgBoards, setLoadingCrossOrgBoards] = useState(false)
  const [referralPartners, setReferralPartners] = useState([])
  const partnerEnterpriseId = partnerDefaultEnterpriseId()
  const [loadingReferralPartnersBoards, setLoadingReferralPartnersBoards] = useState(false)
  const [referralPartnerBoardDialogVisible, setReferralPartnerBoardDialogVisible] = useState(false)

  const getOrganizationName = (organizationId) => {
    if (organizationId === userCtx.session.user.enterprise_id) {
      return userCtx.session.user.enterprise_name
    } else if (organizationId in organizationsDic) {
      return organizationsDic[organizationId]
    }
    return organizationId
  }

  const { refetch } = useQuery(GET_STAFF_NAMES, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      staff_ids: [userCtx.session.user.staff_id]
    },
    skip: true,
  })

  const getStaffsNames = async (staffIds) => {
    if (staffIds.length > 0) {
      const res = await refetch({
        staff_id: userCtx.session.user.staff_id,
        organization_id: userCtx.session.user.organization_id,
        staff_ids: [...new Set(staffIds)]
      })
      if (res.data.getStaffNamesByIds?.data) {
        return JSON.parse(res.data.getStaffNamesByIds.data)
      }
    }
    return {}
  }

  const actualStaffName = `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`

  const getCoordinationBoards = useCallback(() => {
    if (userCtx.session.user.organization_id) {
      setLoading(true)
      const config = {
        headers: {
          Authorization: `Bearer ${userCtx.tokenCoordinationBoards}`,
        }
      }
      axios
        .post(`${api_url}/boards/staffBoards`,
          {
            enterpriseId: userCtx.session.user.enterprise_id,
            organizationIds: organizations.map(o => o.id),
            getAll: true
          },
          config)
        .then(async (response) => {
          if (response.status === 200) {
            let boards = await response.data
            const staffIds = boards.filter(board => board.updatedBy != null).map(board => board.updatedBy)
            const names = await getStaffsNames(staffIds)
            boards = boards.map((board) => {
              if (board.updatedBy != null) {
                board.updatedBy = findName(board.updatedBy, names).full_name
              }
              return board
            })
            setBoards(boards)
          }
          setLoading(false)
        })
        .catch(function (response) {
          setLoading(false)
          const errors = response["response"]["data"]["errors"]
          showError(toast, "Not able to get Board", errors)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations])

  useEffect(() => {
    if (organizations.length > 0) {
      getCoordinationBoards()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations])

  const sortOrganizations = (a, b) => {
    if (a.label < b.label) {
      return -1
    }
    if (a.label > b.label) {
      return 1
    }
    return 0
  }

  const getOrgs = useQuery(GET_ORGANIZATIONS_AND_REFERRALS, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      service_staff_id: "-8",
      partner_enterprise_id: partnerEnterpriseId,
    },
    context: {
      headers: {},
    },
    skip: !userCtx.session,
    fetchPolicy: "network-only",
    onCompleted: ({ organizations, referralOrganizations }) => {
      const orgsDic = organizations.reduce((acc, o) => {
        acc[o.id] = o.name
        return acc
      }, {})

      const orgs = organizations
        .filter(o => o.id !== userCtx.session.user.enterprise_id && !referralOrganizations.some(r => r.id === o.id))
        .map((o) => ({ id: o.id, name: o.name }))
        .sort(sortOrganizations)
      setOrganizations(orgs)

      const referralPartnerOrgs = referralOrganizations
        .filter(r => !organizations.some(o => o.id === r.id))
        .map((r) => ({ id: r.id, name: r.name }))
        .sort(sortOrganizations)
      setReferralPartners(referralPartnerOrgs)

      referralPartnerOrgs.filter(r => !(r.id in orgsDic)).forEach((r) => orgsDic[r.id] = r.name)
      setOrganizationsDic(orgsDic)

    },
  })

  const addBoard = async (board) => {
    if (board.type === BoardType["Cross Organization"]) {
      setLoadingCrossOrgBoards(true)
      if (board.updatedBy != null) {
        board.updatedBy = actualStaffName
      }
      const updatedBoards = [...boards, board]
      setBoards(updatedBoards)
      setLoadingCrossOrgBoards(false)
    } else if (board.type === BoardType["Referral Partner"]) {
      setLoadingReferralPartnersBoards(true)
      if (board.updatedBy != null) {
        board.updatedBy = actualStaffName
      }
      const updatedBoards = [...boards, board]
      setBoards(updatedBoards)
      setLoadingReferralPartnersBoards(false)
    }
  }

  const updateBoard = async (boardId, enabled, readOnly, statusChange) => {
    //Optimistic update
    const board = boards.find((b) => b.id === boardId)
    const updatedBy = actualStaffName
    const updatedAt = getActualDate()
    const updatedBoards = boards.map((b) => {
      if (b.id === boardId) {
        return { ...b, enabled, readOnly, statusChange, updatedBy: updatedBy, updatedAt: updatedAt }
      }
      return b
    })
    setBoards(updatedBoards)
    //-----------------
    const config = {
      headers: {
        Authorization: `Bearer ${userCtx.tokenCoordinationBoards}`,
      }
    }
    const data = {
      id: boardId,
      enabled,
      readOnly,
      statusChange,
      updatedBy: userCtx.session.user.staff_id,
    }
    axios
      .put(`${api_url}/boards`,
        data,
        config)
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        const updatedBoards = boards.map((b) => {
          if (b.id === boardId) {
            return board
          }
          return b
        })
        setBoards(updatedBoards)
        showError(toast, "Not able to update Board", errors)
      })
  }

  const renderBoardActions = (item, enabledBtn) => {
    return (
      <>
        <td>
          <Dropdown
            value={item.statusChange}
            className={`${styles.topic_status_change_dropdown} custom_dropdown`}
            options={topicStatusChangeOptions()}
            panelClassName="custom_dropdown_panel"
            onChange={(e) => {
              updateBoard(item.id, item.enabled, item.readOnly, e.value)
            }}
            optionLabel="label"
            optionValue="value"
          />
        </td>
        <td>
          <div className={`${styles.table_actions} ${styles.c_boards_actions}`}>
            {enabledBtn &&
              <div className={styles.chkbox}>
                <Checkbox
                  inputId="enabled"
                  name="category"
                  className={`${styles.checkbox_design} ${styles.checkbox_border}`}
                  value={item.enabled}
                  onChange={(e) => {
                    updateBoard(item.id, e.checked, item.readOnly, item.statusChange)
                  }}
                  checked={item.enabled}
                />
                <label htmlFor="enabled" style={{ marginLeft: "7px" }}>
                  Enabled
                </label>
              </div>
            }
            <div className={styles.chkbox}>
              <Checkbox
                inputId="readonly"
                name="category"
                className={`${styles.checkbox_design} ${styles.checkbox_border}`}
                value={item.readOnly}
                onChange={(e) => {
                  updateBoard(item.id, item.enabled, e.checked, item.statusChange)
                }}
                checked={item.readOnly}
              />
              <label htmlFor="readonly" style={{ marginLeft: "7px" }}>
                Read-only
              </label>
            </div>
          </div>
        </td>
      </>
    )
  }

  const mainContent = () => {
    return (
      <>
        <Toast ref={toast} />
        <div className={styles.flex_card}>
          <div className={styles.rounded_wrapper}>
            <div className={styles.table_group}>
              <h3
                className={styles.view_subtitle}
                style={{
                  color: "#383838",
                  fontWeight: "600",
                  marginBottom: "10px",
                }}
              >
                Enterprise Board
              </h3>
              {boards.filter((b) => b.type === BoardType.Enterprise).map((item, index) => {
                return (
                  <table
                    className={`${styles.table} ${styles.board_config_table}`}
                    key={`enterprise${index}`}
                  >
                    <thead>
                      <tr>
                        <th key="organization_name_th" style={{ width: "32%", paddingBottom: "0px" }}>
                          Organization
                        </th>
                        <th key="updated_by_th" style={{ width: "22%", paddingBottom: "0px" }}>
                          Last Updated By
                        </th>
                        <th key="updated_at_th" style={{ width: "13%", paddingBottom: "0px" }}>
                          Last Updated On
                        </th>
                        <th key="disc_status_change_th" style={{ minWidth: "12%", paddingBottom: "0" }} >
                          Discussion status change
                        </th>
                        <th key="actions_th" style={{ minWidth: "22%", paddingBottom: "0" }} />
                      </tr>
                    </thead>
                    <tbody>
                      <tr key={index}>
                        {renderTableDataCell({
                          label: "organization_name",
                          value: getOrganizationName(item.ownerEnterpriseId),
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "last_updated_by",
                          value: item.updatedBy,
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "last_updated_on",
                          value: item.updatedAt,
                          type: "datetime",
                        })}
                        {renderBoardActions(item, true)}
                      </tr>
                    </tbody>
                  </table>
                )
              })}
            </div>
            <div className={styles.table_group}>
              <h3
                className={styles.view_subtitle}
                style={{
                  color: "#383838",
                  fontWeight: "600",
                  marginBottom: "10px",
                }}
              >
                Default Boards
              </h3>
              {boards.filter((b) => b.type === BoardType.Default).map((item, index) => {
                return (
                  <table
                    className={`${styles.table} ${styles.board_config_table}`}
                    key={`default${index}`}
                  >
                    <thead>
                      <tr>
                        <th key="organization_name_th" style={{ width: "38%", paddingBottom: "0" }}>
                          Organization
                        </th>
                        <th key="updated_by_th" style={{ width: "22%", paddingBottom: "0" }}>
                          Last Updated By
                        </th>
                        <th key="updated_at_th" style={{ width: "16%", paddingBottom: "0" }}>
                          Last Updated On
                        </th>
                        <th key="disc_status_change_th" style={{ minWidth: "8%", paddingBottom: "0" }} >
                          Discussion status change
                        </th>
                        <th key="actions_th" style={{ minWidth: "20%", paddingBottom: "0" }} />
                      </tr>
                    </thead>
                    <tbody>
                      <tr key={index}>
                        {renderTableDataCell({
                          label: "organization_name",
                          value: getOrganizationName(item.ownerOrganizationId),
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "last_updated_by",
                          value: item.updatedBy,
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "last_updated_on",
                          value: item.updatedAt,
                          type: "datetime",
                        })}
                        {renderBoardActions(item, false)}
                      </tr>
                    </tbody>
                  </table>
                )
              })}
            </div>
          </div>
          <div className={styles.rounded_wrapper}>
            <div className={styles.table_group}>
              <ReferralPartnerBoardForm
                dialogVisible={referralPartnerBoardDialogVisible}
                setDialogVisible={setReferralPartnerBoardDialogVisible}
                organizations={organizations}
                referralPartners={referralPartners}
                addBoard={addBoard}
                toast={toast}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <h3
                  className={styles.view_subtitle}
                  style={{
                    color: "#383838",
                    fontWeight: "600",
                    alignSelf: "center",
                  }}
                >
                  Referral Partner Boards
                </h3>
                <Button
                  label="Configure new board"
                  className={styles.inverted_button}
                  type="submit"
                  onClick={() => {
                    setReferralPartnerBoardDialogVisible(true)
                  }}
                />
              </div>
              {loadingReferralPartnersBoards && <LoaderMedium></LoaderMedium>}
              {!loadingReferralPartnersBoards && boards.filter((b) => b.type === BoardType["Referral Partner"]).length > 0 &&
                boards.filter((b) => b.type === BoardType["Referral Partner"]).map((item, index) => {
                  return (
                    <table
                      className={`${styles.table} ${styles.board_config_table}`}
                      key={`referralPartner${index}`}
                    >
                      <thead>
                        <tr>
                          <th key="organization_name_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Organization
                          </th>
                          <th key="referralPartner_name_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Referral Partner
                          </th>
                          <th key="updated_by_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Last Updated By
                          </th>
                          <th key="updated_at_th" style={{ width: "13%", paddingBottom: "0" }}>
                            Last Updated On
                          </th>
                          <th key="disc_status_change_th" style={{ minWidth: "8%", paddingBottom: "0" }} >
                            Discussion status change
                          </th>
                          <th key="actions_th" style={{ minWidth: "20%", paddingBottom: "0" }} />
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={index}>
                          {renderTableDataCell({
                            label: "organization_name",
                            value: getOrganizationName(item.ownerOrganizationId),
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "referral_name",
                            value: getOrganizationName(item.organizations[0]),
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "last_updated_by",
                            value: item.updatedBy,
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "last_updated_on",
                            value: item.updatedAt,
                            type: "datetime",
                          })}
                          {renderBoardActions(item, true)}
                        </tr>
                      </tbody>
                    </table>
                  )
                })
              }
              {!loadingReferralPartnersBoards && boards.filter((b) => b.type === BoardType["Referral Partner"]).length === 0 &&
                <div style={{ backgroundColor: "#E7E0F1", height: "78px", borderRadius: "12px", display: "flex", alignItems: "center", paddingLeft: "20px" }}>
                  <p>You don't have Referral Partners Boards yet</p>
                </div>
              }
            </div>
          </div>
          <div className={styles.rounded_wrapper}>
            <div className={styles.table_group}>
              <CrossOrganizationBoardForm
                dialogVisible={crossOrgBoardDialogVisible}
                setDialogVisible={setCrossOrgBoardDialogVisible}
                organizations={organizations}
                addBoard={addBoard}
                toast={toast}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <h3
                  className={styles.view_subtitle}
                  style={{
                    color: "#383838",
                    fontWeight: "600",
                    alignSelf: "center",
                  }}
                >
                  Cross-Organization Boards
                </h3>
                <Button
                  label="Configure new board"
                  className={styles.inverted_button}
                  type="submit"
                  onClick={() => {
                    setCrossOrgBoardDialogVisible(true)
                  }}
                />
              </div>
              {loadingCrossOrgBoards && <LoaderMedium></LoaderMedium>}
              {!loadingCrossOrgBoards && boards.filter((b) => b.type === BoardType["Cross Organization"]).length > 0 &&
                boards.filter((b) => b.type === BoardType["Cross Organization"]).map((item, index) => {
                  return (
                    <table
                      className={`${styles.table} ${styles.board_config_table}`}
                      key={`crossOrg${index}`}
                    >
                      <thead>
                        <tr>
                          <th key="organization_name_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Organization #1
                          </th>
                          <th key="organization_name_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Organization #2
                          </th>
                          <th key="updated_by_th" style={{ width: "18%", paddingBottom: "0" }}>
                            Last Updated By
                          </th>
                          <th key="updated_at_th" style={{ width: "13%", paddingBottom: "0" }}>
                            Last Updated On
                          </th>
                          <th key="disc_status_change_th" style={{ minWidth: "8%", paddingBottom: "0" }} >
                            Discussion status change
                          </th>
                          <th key="actions_th" style={{ minWidth: "22%", paddingBottom: "0" }} />
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={index}>
                          {renderTableDataCell({
                            label: "organization_name1",
                            value: getOrganizationName(item.organizations[0]),
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "organization_name2",
                            value: getOrganizationName(item.organizations[1]),
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "last_updated_by",
                            value: item.updatedBy,
                            type: "string",
                          })}
                          {renderTableDataCell({
                            label: "last_updated_on",
                            value: item.updatedAt,
                            type: "datetime",
                          })}
                          {renderBoardActions(item, true)}
                        </tr>
                      </tbody>
                    </table>
                  )
                })
              }
              {!loadingCrossOrgBoards && boards.filter((b) => b.type === BoardType["Cross Organization"]).length === 0 &&
                <div style={{ backgroundColor: "#E7E0F1", height: "78px", borderRadius: "12px", display: "flex", alignItems: "center", paddingLeft: "20px" }}>
                  <p>You don't have Cross Organization Boards yet</p>
                </div>
              }
            </div>
          </div>
        </div>
      </>
    )
  }
  if (loading || getOrgs.loading) {
    return <LoaderMedium></LoaderMedium>
  }
  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <FBTSidebar />
      </div>
      <div className="main-content">
        <h1 className={styles.title}>Coordination Boards</h1>
        {mainContent()}
      </div>
    </div>
  )
}
export default Configuration
